// TODO(ADV-801): unify into a single backend /search endpoint when the
// cross-entity search index lands. Until then we fan out client-side to the
// five per-entity list endpoints that already accept a `search` query param.
// Entities NOT yet covered (their list endpoints lack a `search` param):
//   • team members  — apps/api/src/modules/core/routes.ts (team-members)
//   • locations     — apps/api/src/modules/core/routes.ts (locations)
//   • proposals     — apps/api/src/modules/sales/routes.ts (proposals)
//   • invoices      — apps/api/src/modules/finance/routes.ts (invoices)
//   • jobs          — apps/api/src/modules/ops/routes.ts (jobs)
// Each gap moves into the unified index with ADV-801; this hook's signature is
// the swap surface (consumers stay untouched).

import {
  useSearchContacts,
  useSearchCrews,
  useSearchCustomers,
  useSearchOpportunities,
  useSearchProducts,
} from "@/hooks/use-api";
import {
  contactHrefFor,
  crewHrefFor,
  customerHrefFor,
  opportunityHrefFor,
  productHrefFor,
} from "@/lib/record-hrefs";
import type { ContactSearchResult } from "@adva/types";

type RecordEntityType =
  | "customer"
  | "contact"
  | "opportunity"
  | "product"
  | "crew";

export interface RecordHit {
  /** Entity-prefixed stable id, e.g. `customer:cust-1`. */
  id: string;
  entity: RecordEntityType;
  label: string;
  sublabel?: string;
  href: string;
}

/** Minimum query length before we hit any search endpoint. */
const MIN_QUERY = 2;

interface RawHit {
  id?: unknown;
  contact_id?: unknown;
  name?: unknown;
  display_name?: unknown;
}

function labelForRaw(raw: RawHit, fallback: string): string {
  if (typeof raw.display_name === "string" && raw.display_name.length > 0) {
    return raw.display_name;
  }
  if (typeof raw.name === "string" && raw.name.length > 0) {
    return raw.name;
  }
  return fallback;
}

function idForRaw(raw: RawHit): string | null {
  return typeof raw.id === "string" && raw.id.length > 0 ? raw.id : null;
}

// Customer rows come from CustomerUnifiedSchema and key on `contact_id`, not
// `id` (packages/types/src/schemas/contact.schema.ts). Fall back to `id` only
// for legacy/test rows.
function customerIdForRaw(raw: RawHit): string | null {
  if (typeof raw.contact_id === "string" && raw.contact_id.length > 0) {
    return raw.contact_id;
  }
  return idForRaw(raw);
}

function contactLabel(c: ContactSearchResult): string {
  return (
    [c.first_name, c.last_name].filter(Boolean).join(" ") ||
    c.company_name ||
    c.email ||
    "Contact"
  );
}

interface UseRecordSearchResult {
  results: RecordHit[];
  isLoading: boolean;
  /** True when at least one of the per-entity fan-out queries errored. The
   *  palette can use this to distinguish "no matches" from "search partially
   *  unavailable" (verify finding ADV-805). */
  isError: boolean;
}

/**
 * Multi-entity client-side record search for the ⌘K palette. Fans out to
 * five per-entity list endpoints in parallel and collapses results into a
 * stable `RecordHit[]` in entity order (customer → contact → opportunity →
 * product → crew). Returns empty + isLoading=false when the trimmed query
 * is shorter than {@link MIN_QUERY} characters — caller decides whether to
 * render the records group at all.
 */
export function useRecordSearch(q: string): UseRecordSearchResult {
  const trimmed = q.trim();
  const active = trimmed.length >= MIN_QUERY;
  // Each per-entity hook is internally gated on q.length >= 2; we always
  // call them so React's hook rules are satisfied, then short-circuit at
  // the consumption boundary below.
  const customers = useSearchCustomers(trimmed);
  const contacts = useSearchContacts(trimmed);
  const opportunities = useSearchOpportunities(trimmed);
  const products = useSearchProducts(trimmed);
  const crews = useSearchCrews(trimmed);

  if (!active) {
    return { results: [], isLoading: false, isError: false };
  }

  const results: RecordHit[] = [];

  const customerRows = (customers.data?.customers ?? []) as RawHit[];
  for (const row of customerRows) {
    const id = customerIdForRaw(row);
    if (!id) continue;
    results.push({
      id: `customer:${id}`,
      entity: "customer",
      label: labelForRaw(row, "Customer"),
      href: customerHrefFor(id),
    });
  }

  const contactRows = (contacts.data?.results ?? []) as ContactSearchResult[];
  for (const c of contactRows) {
    results.push({
      id: `contact:${c.contact_id}`,
      entity: "contact",
      label: contactLabel(c),
      sublabel: c.email ?? c.company_name ?? undefined,
      href: contactHrefFor(c),
    });
  }

  const opportunityRows = (opportunities.data?.opportunities ?? []) as RawHit[];
  for (const row of opportunityRows) {
    const id = idForRaw(row);
    if (!id) continue;
    results.push({
      id: `opportunity:${id}`,
      entity: "opportunity",
      label: labelForRaw(row, "Opportunity"),
      href: opportunityHrefFor(id),
    });
  }

  const productRows = (products.data?.products ?? []) as RawHit[];
  for (const row of productRows) {
    const id = idForRaw(row);
    if (!id) continue;
    results.push({
      id: `product:${id}`,
      entity: "product",
      label: labelForRaw(row, "Product"),
      href: productHrefFor(id),
    });
  }

  const crewRows = (crews.data?.crews ?? []) as RawHit[];
  for (const row of crewRows) {
    const id = idForRaw(row);
    if (!id) continue;
    results.push({
      id: `crew:${id}`,
      entity: "crew",
      label: labelForRaw(row, "Crew"),
      href: crewHrefFor(id),
    });
  }

  const isLoading =
    customers.isLoading ||
    contacts.isLoading ||
    opportunities.isLoading ||
    products.isLoading ||
    crews.isLoading;

  const isError = Boolean(
    customers.isError ||
    contacts.isError ||
    opportunities.isError ||
    products.isError ||
    crews.isError,
  );

  return { results, isLoading, isError };
}
